import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Bridge between the main React app and the offline submission queue managed
 * by the service worker. Exposes:
 *
 *   isOnline        — live browser online/offline state
 *   queueCount      — number of pending (retryable) submissions
 *   failedCount     — number of submissions that have given up retrying
 *   failedItems     — details of failed items (path, lastError, ...) for the UI
 *   lastSyncResult  — last sync outcome (auto-cleared after 5s)
 *   retryFailed()   — user-triggered retry for all failed submissions
 *   dismissFailed() — user-triggered "drop these, I'll redo them manually"
 *
 * Why the extra complexity: the previous implementation relied entirely on
 * browser events (`online` / Background Sync) to trigger the queue drain.
 * Those fire unreliably (Safari has no Background Sync; the `online` event
 * does NOT fire on a normal page load even if the browser was offline the
 * previous session). So we now also kick the queue on mount whenever we
 * find it non-empty, and poll for status updates so the banner reflects
 * what's actually in the SW cache.
 */
export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [queueCount, setQueueCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [failedItems, setFailedItems] = useState([]);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  const pollRef = useRef(null);

  const postToSW = useCallback((msg) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(msg);
    }
  }, []);

  const requestStatus = useCallback(() => postToSW('GET_QUEUE_STATUS'), [postToSW]);
  const triggerProcess = useCallback(() => postToSW('PROCESS_OFFLINE_QUEUE'), [postToSW]);

  const retryFailed = useCallback(() => {
    postToSW('RETRY_FAILED_ITEMS');
    // Optimistically move failed counter → pending for instant UI feedback
    setQueueCount((c) => c + failedCount);
    setFailedCount(0);
    setFailedItems([]);
  }, [postToSW, failedCount]);

  const dismissFailed = useCallback(() => {
    postToSW('CLEAR_FAILED_ITEMS');
    setFailedCount(0);
    setFailedItems([]);
  }, [postToSW]);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    triggerProcess();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        if ('sync' in reg) {
          reg.sync.register('offline-queue-sync').catch(() => {});
        }
      });
    }
  }, [triggerProcess]);

  const handleOffline = useCallback(() => setIsOnline(false), []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const handleMessage = (event) => {
      if (event.data?.type === 'OFFLINE_QUEUED') {
        setQueueCount((c) => c + 1);
      }
      if (event.data?.type === 'QUEUE_STATUS') {
        setQueueCount(event.data.count || 0);
        setFailedCount(event.data.failed || 0);
        setFailedItems(event.data.failedItems || []);
      }
      if (event.data?.type === 'OFFLINE_SYNC_COMPLETE') {
        setQueueCount(event.data.remaining || 0);
        setFailedCount(event.data.failed || 0);
        setFailedItems(event.data.failedItems || []);
        // Only show the green success toast if at least one submission
        // actually succeeded. Prevents the "Synced 0 items" nonsense.
        const succeeded = (event.data.results || []).filter((r) => r.success).length;
        if (succeeded > 0) {
          setLastSyncResult(event.data);
          setTimeout(() => setLastSyncResult(null), 5000);
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
      // First status poll on mount — also kicks a process pass if the queue
      // has pending items (covers the "page-reload-while-online" case that
      // was leaving submissions stranded forever).
      requestStatus();
      if (navigator.onLine) triggerProcess();
    }

    // Poll every 30s so the banner reflects retries the SW does on its own
    // schedule (e.g. a 2-minute backoff window). Cheap — just a postMessage.
    pollRef.current = setInterval(() => {
      requestStatus();
      if (navigator.onLine) triggerProcess();
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [handleOnline, handleOffline, requestStatus, triggerProcess]);

  return {
    isOnline,
    queueCount,
    failedCount,
    failedItems,
    lastSyncResult,
    retryFailed,
    dismissFailed,
  };
}
